import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useCookies } from 'react-cookie';

interface CookieUser {
  email?: string;
  company_id?: string;
  accepted_terms?: boolean;
  super_user?: boolean;
  company_scope?: string;
  [key: string]: unknown;
}

interface PrivateRouteProps {
  element: React.ElementType;
  adminOnly?: boolean;
  excludeReadOnly?: boolean;
  resellerOnly?: boolean;
}

interface Cookies {
  user?: CookieUser;
  token?: string;
  super_user?: boolean;
  role?: string;
  [key: string]: unknown;
}

const PrivateRoute: React.FC<PrivateRouteProps> = ({
  element: Component,
  adminOnly = false,
  excludeReadOnly = false,
  resellerOnly = false,
}) => {
  const [cookies] = useCookies(['user', 'super_user', 'token', 'role']);
  const typedCookies = cookies as Cookies;
  const location = useLocation();

  if (!typedCookies.token) {
    return <Navigate to="/login" />;
  }

  if (!typedCookies.user) {
    return <Navigate to="/login" />;
  }

  if (adminOnly && !typedCookies.super_user) {
    return <Navigate to="/login" />;
  }

  if (
    resellerOnly &&
    !(
      typedCookies.user?.company_scope === 'RESELLER' ||
      typedCookies.super_user
    )
  ) {
    return <Navigate to="/" />;
  }

  // Check if read-only users should be excluded from this route
  // Exclude READ_ONLY user role and SPECTATOR company scope
  if (
    excludeReadOnly &&
    (typedCookies.role === 'READ_ONLY' ||
      typedCookies.user?.company_scope === 'SPECTATOR')
  ) {
    return <Navigate to="/" />;
  }

  // Check if user has accepted terms (all users except super users)
  // Skip check if already on accept terms page
  const isSuperUser = typedCookies.super_user || typedCookies.user.super_user;
  const requiresTermsAcceptance =
    !isSuperUser && !typedCookies.user.accepted_terms;

  if (location.pathname !== '/accept-t&c' && requiresTermsAcceptance) {
    return <Navigate to="/accept-t&c" />;
  }

  return <Component />;
};

export default PrivateRoute;
